import { useState } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFTextField } from '../../components/hook-form';
// utils
import axios from '../../utils/axios';
// components
import Iconify from '../../components/Iconify';
import DIOText from '../../components/DIOText';


export default function EmailAccountCreateDialog({ open, onClose, onCreated }) {
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const EmailAccountSchema = Yup.object().shape({
    local_part: Yup.string()
      .required('이메일 아이디를 입력해주세요')
      .matches(
        /^[a-z0-9._-]+$/,
        '영문 소문자, 숫자, 특수문자(._-)만 사용 가능합니다'
      )
      .min(3, '최소 3자 이상 입력해주세요')
      .max(30, '최대 30자까지 입력 가능합니다'),
    password: Yup.string()
      .required('비밀번호를 입력해주세요')
      .min(8, '비밀번호는 최소 8자 이상이어야 합니다'),
  });

  const defaultValues = {
    local_part: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(EmailAccountSchema),
    defaultValues,
  });

  const { reset, handleSubmit, setError } = methods;

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const response = await axios.post('/api/crews/emails/account/create/', data);

      enqueueSnackbar('이메일 계정이 생성되었습니다', { variant: 'success' });
      reset();
      if (onCreated) {
        onCreated(response.data.email);
      }
      onClose();
    } catch (error) {
      console.error(error);

      if (error.response?.status === 409) {
        if (error.response?.data?.error_code === 'EMAIL_ALREADY_EXISTS') {
          setError('local_part', {
            type: 'manual',
            message: '이미 사용 중인 아이디입니다. 다른 아이디를 입력해주세요.',
          });
        } else {
          enqueueSnackbar('이미 이메일 계정을 보유하고 있습니다', { variant: 'warning' });
          onClose();
        }
      } else {
        enqueueSnackbar(
          error.response?.data?.error || '이메일 계정 생성 중 오류가 발생했습니다',
          { variant: 'error' }
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">크루 사이드잡 이메일 계정 생성</Typography>
          {!isSubmitting && (
            <Button onClick={handleClose} sx={{ minWidth: 'auto', p: 1 }}>
              <Iconify icon="eva:close-fill" width={24} height={24} />
            </Button>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <DIOText /> 사이드잡 업무용 이메일 계정(@diocrew.com)을 생성합니다.
            </Typography>

            <RHFTextField
              name="local_part"
              label="이메일 아이디"
              placeholder="ID를 입력해주세요"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">
                      @diocrew.com
                    </Typography>
                  </InputAdornment>
                ),
              }}
              helperText="영문 소문자, 숫자, 특수문자(._-)만 사용 가능합니다"
            />

            <RHFTextField
              name="password"
              label="비밀번호"
              type={showPassword ? 'text' : 'password'}
              placeholder="비밀번호를 입력해주세요"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText="최소 8자 이상 입력해주세요"
            />

            <Stack direction="row" spacing={2}>
              <Button fullWidth size="large" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                취소
              </Button>
              <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
                계정 생성
              </LoadingButton>
            </Stack>
          </Stack>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
